import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { getTablesWithOrders } from "../../../../../services/Admin/SalesScreenApi";
import TableOrderInfo from "../OrderDetailsInfo";
import OpenOrderDialog from "../OrderActions/OpenOrder/OpenOrderDialog";
import TablesOrdersCard from "./OrderCard/TablesOrdersCard";

const TablesOrder = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openDetails, setOpenDetails] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState(null);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedTableForOpen, setSelectedTableForOpen] = useState(null);
  
  const [searchText, setSearchText] = useState("");
  const [filterCaptain, setFilterCaptain] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterTableStatus, setFilterTableStatus] = useState(""); // متاحة أو مشغولة
  const [underReviewOnly, setUnderReviewOnly] = useState(false);
  const isInitialLoading = loading && rows.length === 0;

  const fetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTablesWithOrders();
      setRows(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load tables with orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  // الحصول على قوائم فريدة للفلاتر
  const uniqueCaptains = [...new Set(
    rows
      .map(table => table.active_order?.captain_order?.name)
      .filter(Boolean)
  )];
  
  const uniqueUsers = [...new Set(
    rows
      .map(table => table.active_order?.user?.name)
      .filter(Boolean)
  )];
  
  const uniqueStatuses = [...new Set(
    rows
      .map(table => table.active_order?.status)
      .filter(Boolean)
  )];

  const uniqueTypes = [...new Set(
    rows
      .map(table => table.active_order?.type)
      .filter(Boolean)
  )];

  const underReviewCount = useMemo(() => {
    return rows.filter((table) => table?.active_order?.status === "under_review").length;
  }, [rows]);

  // فلترة الطاولات بناءً على البحث والفلاتر
  const filteredRows = useMemo(() => {
    return rows.filter((table) => {
      const order = table.active_order;

      if (underReviewOnly && order?.status !== "under_review") {
        return false;
      }

      // فلتر حالة الطاولة (متاحة/مشغولة)
      if (filterTableStatus === "available" && order) return false;
      if (filterTableStatus === "busy" && !order) return false;

      // إذا لم يكن هناك طلب نشط، لا نطبق باقي الفلاتر
      if (!order) return true;

      // فلتر البحث النصي
      let matchesSearch = true;
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const customer = order.customer;
        
        matchesSearch = (
          table.name?.toLowerCase().includes(searchLower) ||
          customer?.name?.toLowerCase().includes(searchLower) ||
          customer?.phone?.toLowerCase().includes(searchLower) ||
          customer?.phone2?.toLowerCase().includes(searchLower) ||
          customer?.phone3?.toLowerCase().includes(searchLower) ||
          customer?.email?.toLowerCase().includes(searchLower) ||
          order.general_increment?.toString().includes(searchText) ||
          order.daily_increment?.toString().includes(searchText) ||
          order.monthly_increment?.toString().includes(searchText)
        );
      }

      // فلتر الكابتن
      const matchesCaptain = !filterCaptain || order.captain_order?.name === filterCaptain;
      
      // فلتر المستخدم
      const matchesUser = !filterUser || order.user?.name === filterUser;
      
      // فلتر حالة الطلب
      const matchesStatus = !filterStatus || order.status === filterStatus;

      // فلتر نوع الطلب من المصدر (client/local/screen)
      const matchesType = !filterType || order.type === filterType;

      return (
        matchesSearch &&
        matchesCaptain &&
        matchesUser &&
        matchesStatus &&
        matchesType
      );
    });
  }, [rows, searchText, filterCaptain, filterUser, filterStatus, filterType, filterTableStatus, underReviewOnly]);

  const handleUnderReviewAlertClick = () => {
    if (underReviewOnly) {
      setUnderReviewOnly(false);
      setFilterStatus("");
      return;
    }

    setUnderReviewOnly(true);
    setFilterStatus("under_review");
    setFilterTableStatus("busy");
    setSearchText("");
    setFilterCaptain("");
    setFilterUser("");
    setFilterType("");
  };

  // ✅ دايمًا بنجيب أحدث نسخة من الـ table من rows
  const selectedTable = useMemo(() => {
    if (!selectedTableId) return null;
    return rows.find((t) => t.id === selectedTableId) || null;
  }, [rows, selectedTableId]);

  const handleOpenDetails = (table) => {
    if (!table?.active_order) {
      // إذا لم يكن هناك طلب نشط، افتح حوار إنشاء طلب جديد
      handleOpenTable(table);
      return;
    }
    setSelectedTableId(table.id);
    setOpenDetails(true);
  };

  const handleOpenTable = (table) => {
    setSelectedTableForOpen(table || null);
    setOpenCreateDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedTableId(null);
  };

  if (isInitialLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );

  if (error) return <Box sx={{ color: "error.main", p: 2 }}>{String(error)}</Box>;

  return (
    <Box>
      {loading && rows.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {underReviewCount > 0 && (
        <Alert
          severity="warning"
          variant="filled"
          onClick={handleUnderReviewAlertClick}
          sx={{
            mb: 2,
            cursor: "pointer",
            userSelect: "none",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <AlertTitle>
            يوجد {underReviewCount} طلب بانتظار المراجعة
          </AlertTitle>
          {underReviewOnly
            ? "جاري عرض طلبات (طلب خارجي) فقط. اضغط لإلغاء الفلتر."
            : "اضغط هنا لعرض كل الطلبات التي حالتها (طلب خارجي)."}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* شريط البحث */}
        <TextField
          placeholder="ابحث بالاسم، رقم الهاتف، رقم الطلب، اسم الطاولة..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* فلتر الكابتن */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>الكابتن</InputLabel>
          <Select
            value={filterCaptain}
            onChange={(e) => setFilterCaptain(e.target.value)}
            label="الكابتن"
          >
            <MenuItem value="">الكل</MenuItem>
            {uniqueCaptains.map((captain) => (
              <MenuItem key={captain} value={captain}>{captain}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* فلتر المستخدم */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>المستخدم</InputLabel>
          <Select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            label="المستخدم"
          >
            <MenuItem value="">الكل</MenuItem>
            {uniqueUsers.map((user) => (
              <MenuItem key={user} value={user}>{user}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* فلتر حالة الطلب */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>حالة الطلب</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="حالة الطلب"
          >
            <MenuItem value="">الكل</MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status === "pending" ? "قيد التجهيز" :
                 status === "in_prepration" || status === "in_preparation" ? "في التحضير" :
                 status === "pickup" ? "تم الاستلام من العميل" :
                 status === "out_of_preparation" ? "خارج التحضير" :
                 status === "under_review" ? "تحت المراجعة" :
                 status === "finished" ? "منتهي" :
                 status === "cancelled" ? "ملغي" : status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* فلتر نوع الطلب */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>نوع الطلب</InputLabel>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            label="نوع الطلب"
          >
            <MenuItem value="">الكل</MenuItem>
            {uniqueTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type === "client"
                  ? "تم من خلال العميل"
                  : type === "local"
                  ? "تم من خلال كاشير"
                  : type === "screen"
                  ? "تم من خلال شاشة"
                  : type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* فلتر حالة الطاولة */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>حالة الطاولة</InputLabel>
          <Select
            value={filterTableStatus}
            onChange={(e) => setFilterTableStatus(e.target.value)}
            label="حالة الطاولة"
          >
            <MenuItem value="">الكل</MenuItem>
            <MenuItem value="busy">مشغولة</MenuItem>
            <MenuItem value="available">متاحة</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={1.5}>
        {filteredRows.map((t) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={t.id}>
            <TablesOrdersCard
              table={t}
              onTableClick={handleOpenDetails}
              onOrderUpdated={fetch}
            />
          </Grid>
        ))}
      </Grid>

      {filteredRows.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          {searchText || filterCaptain || filterUser || filterStatus || filterType || filterTableStatus
            ? "لا توجد نتائج للبحث"
            : "لا توجد طاولات"}
        </Box>
      )}

      <Dialog open={openDetails} onClose={handleCloseDetails} fullWidth maxWidth="lg">
        <DialogTitle>تفاصيل طلب الطاولة: {selectedTable?.name || "-"}</DialogTitle>
        <DialogContent dividers>
          {/* ✅ order هنا جاي من selectedTable المحسوب من rows (أحدث نسخة) */}
          <TableOrderInfo 
            order={selectedTable?.active_order} 
            onRefresh={fetch}
            onInvoicePaymentSuccess={handleCloseDetails}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>إغلاق</Button>
        </DialogActions>
      </Dialog>

      <OpenOrderDialog
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          setSelectedTableForOpen(null);
        }}
        onCreated={fetch}
        initialOrderType="tables"
        initialTableId={selectedTableForOpen?.id}
      />
    </Box>
  );
};

export default TablesOrder;
